import React, { useState, useEffect, useCallback } from 'react';
import {
  LogIn, LogOut, MessageSquare, Star, Briefcase, MapPin, Target,
  Image, Pencil, Trash2, Plus, Save, X, Upload, ChevronRight, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// ─── Auth helpers ───
function getToken() { return localStorage.getItem('heft_admin_token'); }
function setToken(t) { localStorage.setItem('heft_admin_token', t); }
function clearToken() { localStorage.removeItem('heft_admin_token'); }

async function api(path, opts = {}) {
  const token = getToken();
  const headers = { ...(opts.headers || {}) };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (!(opts.body instanceof FormData)) headers['Content-Type'] = 'application/json';
  const res = await fetch(`${API}/api/admin${path}`, { ...opts, headers });
  if (res.status === 401) { clearToken(); window.location.reload(); }
  if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || 'Request failed'); }
  return res.json();
}

// ─── Tabs config ───
const TABS = [
  { key: 'enquiries', label: 'Enquiries', icon: MessageSquare },
  { key: 'testimonials', label: 'Testimonials', icon: Star },
  { key: 'services', label: 'Services', icon: Briefcase },
  { key: 'locations', label: 'Locations', icon: MapPin },
  { key: 'goals', label: 'Goals', icon: Target },
  { key: 'images', label: 'Images', icon: Image },
];

// ─── Reusable components ───
function Btn({ children, onClick, variant = 'primary', small, className = '', ...props }) {
  const base = 'inline-flex items-center gap-1.5 font-semibold tracking-wide uppercase transition-all duration-200';
  const sizes = small ? 'text-[10px] px-3 py-1.5' : 'text-xs px-4 py-2.5';
  const variants = {
    primary: 'bg-[#1A1A1A] text-white hover:bg-[#333]',
    danger: 'bg-red-600 text-white hover:bg-red-700',
    ghost: 'text-[#6B6B6B] hover:text-[#1A1A1A] hover:bg-[#F5F0EB]',
    gold: 'bg-[#C9A962] text-white hover:bg-[#B89952]',
  };
  return <button onClick={onClick} className={`${base} ${sizes} ${variants[variant]} ${className}`} {...props}>{children}</button>;
}

function Field({ label, value, onChange, textarea, placeholder }) {
  const cls = 'w-full px-3 py-2 text-sm border border-[#E5E0DB] focus:border-[#C9A962] focus:outline-none bg-white';
  return (
    <div>
      <label className="block text-[10px] font-semibold text-[#6B6B6B] uppercase tracking-wide mb-1">{label}</label>
      {textarea
        ? <textarea value={value} onChange={e => onChange(e.target.value)} className={`${cls} min-h-[80px] resize-y`} placeholder={placeholder} />
        : <input value={value} onChange={e => onChange(e.target.value)} className={cls} placeholder={placeholder} />
      }
    </div>
  );
}

// ─── Login ───
function LoginForm({ onLogin }) {
  const [pw, setPw] = useState('');
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { token } = await fetch(`${API}/api/admin/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: pw }),
      }).then(r => { if (!r.ok) throw new Error('Invalid password'); return r.json(); });
      setToken(token);
      onLogin();
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  };
  return (
    <div className="min-h-screen bg-[#1A1A1A] flex items-center justify-center px-4">
      <form onSubmit={submit} className="bg-white p-8 w-full max-w-sm" data-testid="admin-login-form">
        <h1 className="text-xl font-bold text-[#1A1A1A] mb-1" style={{ fontFamily: "'Oswald', sans-serif" }}>HEFT ADMIN</h1>
        <p className="text-xs text-[#6B6B6B] mb-6">Enter admin password to continue</p>
        <input
          type="password" value={pw} onChange={e => setPw(e.target.value)}
          placeholder="Password" autoFocus data-testid="admin-password-input"
          className="w-full px-4 py-3 mb-4 border border-[#E5E0DB] focus:border-[#C9A962] focus:outline-none text-sm"
        />
        <Btn type="submit" disabled={loading} className="w-full justify-center" data-testid="admin-login-btn">
          {loading ? <Loader2 size={14} className="animate-spin" /> : <LogIn size={14} />}
          {loading ? 'Authenticating...' : 'Sign In'}
        </Btn>
      </form>
    </div>
  );
}

// ─── Enquiries Tab ───
function EnquiriesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { setItems(await api('/enquiries')); } catch {} finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm('Delete this enquiry?')) return;
    await api(`/enquiries/${id}`, { method: 'DELETE' });
    toast.success('Enquiry deleted');
    load();
  };

  if (loading) return <p className="text-sm text-[#6B6B6B] p-4">Loading...</p>;
  if (!items.length) return <p className="text-sm text-[#6B6B6B] p-4">No enquiries yet.</p>;
  return (
    <div className="space-y-3" data-testid="enquiries-list">
      {items.map(e => (
        <div key={e.id} className="bg-white border border-[#E5E0DB] p-4">
          <div className="flex justify-between items-start mb-2">
            <div>
              <p className="text-sm font-semibold text-[#1A1A1A]">{e.name}</p>
              <p className="text-xs text-[#6B6B6B]">{e.email} {e.phone && `| ${e.phone}`}</p>
            </div>
            <Btn variant="danger" small onClick={() => remove(e.id)}><Trash2 size={12} /></Btn>
          </div>
          <div className="flex gap-3 text-[10px] text-[#6B6B6B] mb-2">
            <span className="bg-[#F5F0EB] px-2 py-0.5">{e.location}</span>
            <span className="bg-[#F5F0EB] px-2 py-0.5">{e.goal}</span>
          </div>
          {e.message && <p className="text-xs text-[#6B6B6B]">{e.message}</p>}
          <p className="text-[10px] text-[#9B9B9B] mt-2">{new Date(e.created_at).toLocaleString()}</p>
        </div>
      ))}
    </div>
  );
}

// ─── Generic CRUD Tab ───
function CrudTab({ endpoint, fields, itemLabel }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try { setItems(await api(`/${endpoint}`)); } catch {} finally { setLoading(false); }
  }, [endpoint]);
  useEffect(() => { load(); }, [load]);

  const startNew = () => {
    const blank = {};
    fields.forEach(f => { blank[f.key] = f.type === 'list' ? [] : ''; });
    setForm(blank);
    setEditing('new');
  };

  const startEdit = (item) => {
    setForm({ ...item });
    setEditing(item.id);
  };

  const save = async () => {
    try {
      if (editing === 'new') {
        await api(`/${endpoint}`, { method: 'POST', body: JSON.stringify(form) });
        toast.success(`${itemLabel} created`);
      } else {
        await api(`/${endpoint}/${editing}`, { method: 'PUT', body: JSON.stringify(form) });
        toast.success(`${itemLabel} updated`);
      }
      setEditing(null);
      load();
    } catch (err) { toast.error(err.message); }
  };

  const remove = async (id) => {
    if (!window.confirm(`Delete this ${itemLabel.toLowerCase()}?`)) return;
    await api(`/${endpoint}/${id}`, { method: 'DELETE' });
    toast.success(`${itemLabel} deleted`);
    load();
  };

  if (loading) return <p className="text-sm text-[#6B6B6B] p-4">Loading...</p>;

  return (
    <div data-testid={`${endpoint}-tab`}>
      <div className="flex justify-between items-center mb-4">
        <p className="text-xs text-[#6B6B6B]">{items.length} {itemLabel.toLowerCase()}(s)</p>
        <Btn small variant="gold" onClick={startNew}><Plus size={12} /> Add {itemLabel}</Btn>
      </div>

      {editing && (
        <div className="bg-[#FAFAF8] border border-[#C9A962] p-4 mb-4">
          <p className="text-xs font-semibold text-[#1A1A1A] mb-3 uppercase">{editing === 'new' ? 'New' : 'Edit'} {itemLabel}</p>
          <div className="grid gap-3 sm:grid-cols-2">
            {fields.map(f => f.type === 'list' ? (
              <div key={f.key} className="sm:col-span-2">
                <label className="block text-[10px] font-semibold text-[#6B6B6B] uppercase tracking-wide mb-1">{f.label}</label>
                <textarea
                  value={(form[f.key] || []).join('\n')}
                  onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value.split('\n').filter(Boolean) }))}
                  className="w-full px-3 py-2 text-sm border border-[#E5E0DB] focus:border-[#C9A962] focus:outline-none min-h-[60px] resize-y"
                  placeholder="One item per line"
                />
              </div>
            ) : (
              <div key={f.key} className={f.wide ? 'sm:col-span-2' : ''}>
                <Field label={f.label} value={form[f.key] || ''} onChange={v => setForm(prev => ({ ...prev, [f.key]: v }))} textarea={f.textarea} placeholder={f.placeholder} />
              </div>
            ))}
          </div>
          <div className="flex gap-2 mt-3">
            <Btn small onClick={save}><Save size={12} /> Save</Btn>
            <Btn small variant="ghost" onClick={() => setEditing(null)}><X size={12} /> Cancel</Btn>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {items.map(item => (
          <div key={item.id} className="bg-white border border-[#E5E0DB] p-3 flex items-center justify-between group hover:border-[#C9A962] transition-colors">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-[#1A1A1A] truncate">{item[fields[0].key]}</p>
              {fields[1] && <p className="text-xs text-[#6B6B6B] truncate">{Array.isArray(item[fields[1].key]) ? `${item[fields[1].key].length} items` : item[fields[1].key]}</p>}
            </div>
            <div className="flex gap-1 shrink-0 ml-2">
              <Btn small variant="ghost" onClick={() => startEdit(item)}><Pencil size={12} /></Btn>
              <Btn small variant="danger" onClick={() => remove(item.id)}><Trash2 size={12} /></Btn>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Goals Tab ───
function GoalsTab() {
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try { const g = await api('/goals'); setGoals(g); setDraft(g.join('\n')); } catch {} finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const list = draft.split('\n').map(s => s.trim()).filter(Boolean);
    await api('/goals', { method: 'PUT', body: JSON.stringify({ goals: list }) });
    toast.success('Goals updated');
    load();
  };

  if (loading) return <p className="text-sm text-[#6B6B6B] p-4">Loading...</p>;
  return (
    <div data-testid="goals-tab">
      <p className="text-xs text-[#6B6B6B] mb-2">One goal per line. These appear in the contact form dropdown.</p>
      <textarea
        value={draft} onChange={e => setDraft(e.target.value)}
        className="w-full px-3 py-2 text-sm border border-[#E5E0DB] focus:border-[#C9A962] focus:outline-none min-h-[200px] resize-y mb-3"
      />
      <Btn small onClick={save}><Save size={12} /> Save Goals</Btn>
    </div>
  );
}

// ─── Images Tab ───
function ImagesTab() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setImages(await api('/images')); } catch {} finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const token = getToken();
      const res = await fetch(`${API}/api/admin/upload`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: fd,
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Upload failed'); }
      toast.success('Image uploaded');
      load();
    } catch (err) { toast.error(err.message); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this image?')) return;
    await api(`/images/${id}`, { method: 'DELETE' });
    toast.success('Image deleted');
    load();
  };

  const copyUrl = (path) => {
    const url = `${API}/api/admin/files/${path}`;
    navigator.clipboard.writeText(url);
    toast.success('URL copied to clipboard');
  };

  if (loading) return <p className="text-sm text-[#6B6B6B] p-4">Loading...</p>;
  return (
    <div data-testid="images-tab">
      <div className="flex items-center gap-3 mb-4">
        <label className="cursor-pointer">
          <input type="file" accept="image/*" onChange={upload} className="hidden" />
          <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold tracking-wide uppercase px-3 py-1.5 bg-[#C9A962] text-white hover:bg-[#B89952] transition-colors cursor-pointer">
            {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
            {uploading ? 'Uploading...' : 'Upload Image'}
          </span>
        </label>
        <p className="text-[10px] text-[#9B9B9B]">Max 10MB. JPG, PNG, WebP, GIF.</p>
      </div>

      {!images.length && <p className="text-sm text-[#6B6B6B]">No images uploaded yet.</p>}

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {images.map(img => (
          <div key={img.id} className="bg-white border border-[#E5E0DB] overflow-hidden group hover:border-[#C9A962] transition-colors">
            <div className="aspect-square bg-[#F5F0EB] flex items-center justify-center overflow-hidden">
              <img
                src={`${API}/api/admin/files/${img.storage_path}`}
                alt={img.original_filename}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <div className="p-2">
              <p className="text-[10px] text-[#6B6B6B] truncate mb-1">{img.original_filename}</p>
              <div className="flex gap-1">
                <Btn small variant="ghost" onClick={() => copyUrl(img.storage_path)} title="Copy URL">
                  <ChevronRight size={10} /> URL
                </Btn>
                <Btn small variant="danger" onClick={() => remove(img.id)}>
                  <Trash2 size={10} />
                </Btn>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Main Admin Panel ───
export default function AdminPanel() {
  const [authed, setAuthed] = useState(!!getToken());
  const [tab, setTab] = useState('enquiries');

  useEffect(() => {
    if (!getToken()) return;
    api('/verify').catch(() => { clearToken(); setAuthed(false); });
  }, []);

  if (!authed) return <LoginForm onLogin={() => setAuthed(true)} />;

  return (
    <div className="min-h-screen bg-[#F5F0EB]" data-testid="admin-panel">
      {/* Header */}
      <header className="bg-[#1A1A1A] px-4 py-3 flex items-center justify-between sticky top-0 z-50">
        <h1 className="text-sm font-bold text-white tracking-wide" style={{ fontFamily: "'Oswald', sans-serif" }}>
          HEFT ADMIN
        </h1>
        <Btn small variant="ghost" onClick={() => { clearToken(); setAuthed(false); }} className="!text-white/60 hover:!text-white hover:!bg-white/10">
          <LogOut size={12} /> Sign Out
        </Btn>
      </header>

      <div className="flex flex-col md:flex-row">
        {/* Sidebar */}
        <nav className="md:w-48 bg-white border-b md:border-b-0 md:border-r border-[#E5E0DB] md:min-h-[calc(100vh-52px)]">
          <div className="flex md:flex-col overflow-x-auto md:overflow-visible">
            {TABS.map(t => {
              const Icon = t.icon;
              const active = tab === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  data-testid={`tab-${t.key}`}
                  className={`flex items-center gap-2 px-4 py-3 text-xs font-semibold uppercase tracking-wide transition-colors whitespace-nowrap ${
                    active ? 'text-[#1A1A1A] bg-[#F5F0EB] border-b-2 md:border-b-0 md:border-l-2 border-[#C9A962]' : 'text-[#6B6B6B] hover:text-[#1A1A1A] hover:bg-[#FAFAF8]'
                  }`}
                >
                  <Icon size={14} /> {t.label}
                </button>
              );
            })}
          </div>
        </nav>

        {/* Content */}
        <main className="flex-1 p-4 md:p-6 max-w-4xl">
          <h2 className="text-lg font-bold text-[#1A1A1A] mb-4" style={{ fontFamily: "'Oswald', sans-serif" }}>
            {TABS.find(t => t.key === tab)?.label}
          </h2>

          {tab === 'enquiries' && <EnquiriesTab />}

          {tab === 'testimonials' && (
            <CrudTab
              endpoint="testimonials"
              itemLabel="Testimonial"
              fields={[
                { key: 'name', label: 'Name', placeholder: 'Sarah M.' },
                { key: 'role', label: 'Role', placeholder: 'Marketing Manager' },
                { key: 'content', label: 'Quote', textarea: true, wide: true },
                { key: 'result', label: 'Result', placeholder: 'Lost 8kg and gained confidence' },
                { key: 'duration', label: 'Duration', placeholder: '6 months' },
              ]}
            />
          )}

          {tab === 'services' && (
            <CrudTab
              endpoint="services"
              itemLabel="Service"
              fields={[
                { key: 'title', label: 'Title', placeholder: '1-to-1 Personal Training' },
                { key: 'description', label: 'Description', textarea: true, wide: true },
                { key: 'features', label: 'Features (one per line)', type: 'list', wide: true },
              ]}
            />
          )}

          {tab === 'locations' && (
            <CrudTab
              endpoint="locations"
              itemLabel="Location"
              fields={[
                { key: 'name', label: 'Name', placeholder: 'Anytime Fitness Leyton' },
                { key: 'tag', label: 'Tag', placeholder: 'Base' },
                { key: 'desc', label: 'Description', wide: true },
                { key: 'mapUrl', label: 'Google Maps URL', wide: true },
              ]}
            />
          )}

          {tab === 'goals' && <GoalsTab />}
          {tab === 'images' && <ImagesTab />}
        </main>
      </div>
    </div>
  );
}
